import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import * as helper from '../global/helper';
import { getViewUserAll } from '../api/usersAPI';
import { deleteCashCard, getCashCardList, postCashCard } from '../api/cashCardAPI';
import moment from 'moment';

const CashCardMaster = (props) => {
    // State Management
    const [members, setMembers] = useState([]);
    const [selectedMember, setSelectedMember] = useState(null);
    const [cardNumber, setCardNumber] = useState('');
    const [cashCardList, setCashCardList] = useState([]);
    const [referenceId, setReferenceId] = useState(0);
    const [isUpdate, setIsUpdate] = useState(false);
    const [isInit, setIsInit] = useState(true);
    const [cardBalanceTotal, setCardBalanceTotal] = useState(0);

    // Component initialization
    useEffect(() => {
        props.title("Cash Card Master", "cash-card-master");
        fetchMembers();
    }, []);

    // Fetch cash cards when member is selected
    useEffect(() => {
        if (selectedMember) {
            fetchCashCardList(selectedMember?.value);
        }
    }, [selectedMember]);

    // Calculate total balance when cash card list changes
    useEffect(() => {
        if (cashCardList.length > 0) {
            const total = cashCardList.reduce((sum, card) => sum + parseFloat(card.CardAmt || 0), 0);
            setCardBalanceTotal(total);
        } else {
            setCardBalanceTotal(0);
        }
    }, [cashCardList]);

    // API Calls
    const fetchMembers = async () => {
        try {
            Swal.fire({
                titleText: 'Loading',
                text: 'Please Wait...',
                showConfirmButton: false,
                onOpen: () => {
                    Swal.showLoading();
                }
            });

            const response = await getViewUserAll();
            setMembers(response.map(member => ({
                label: `${member.Name} (${member.MembershipNo})`,
                value: member.ID
            })));
            
            // Set default member if available
            if (response.length > 0) {
                setSelectedMember({
                    label: `${response[0].Name} (${response[0].MembershipNo})`,
                    value: response[0].ID
                });
            }
            Swal.close();
        } catch (error) {
            Swal.close();
            Swal.fire({ 
                icon: 'error', 
                titleText: error.message || 'Failed to load members' 
            });
        }
    };

    const fetchCashCardList = async (userId) => {
        if (!userId) return;
        
        try {
            Swal.fire({
                titleText: 'Loading',
                text: 'Please Wait...',
                showConfirmButton: false,
                onOpen: () => {
                    Swal.showLoading();
                }
            }); 
            
            const data = await getCashCardList(userId);
            
            Swal.close();
            
            if (data.Message === 'success') {
                setIsInit(false);
                if (data.CashCardList && data.CashCardList.length > 0) {
                    setCashCardList(data.CashCardList);
                } else {
                    setCashCardList([]);
                }
            }   
        } catch (error) {
            Swal.close();
            Swal.fire({
                icon: 'error',
                titleText: error.message || 'Failed to load cash cards'
            });
        }
    };

    const handleAddCard = async () => {
        const btn = $('#btnAdd');
        var form = $('#ControlHolder');
        
        form.validate({
            rules: {
                txtCardNumber: {
                    required: true
                },
                ddlMember: {
                    required: true
                }
            },
            messages: {
                txtCardNumber: {
                    required: "This field is required"
                },
                ddlMember: {
                    required: "This field is required"
                }
            }
        }); 
        
        if (!form.valid()) return;
        
        helper.StartProcessing(btn);
        
        // Validate card doesn't already exist
        const cardExists = cashCardList.some(card => card.CardNo === cardNumber && card.ID !== referenceId);
        
        if (cardExists && !isUpdate) {
            helper.StopProcessing(btn);
            Swal.fire({
                icon: 'error',
                titleText: 'Card Already Exists!'
            });
            return;
        }
        
        try {
            const cashCardMaster = {
                ID: referenceId,
                UserID: selectedMember.value,
                UserType: 'User',
                CardNumber: cardNumber
            };
            
            const data = await postCashCard(cashCardMaster);
            helper.StopProcessing(btn);
            
            if (data.Message === 'success') {
                if (data.CardDetails.CardStatus === '1') {
                    if (referenceId === 0) {
                        // Add new card to list
                        setCashCardList(prev => [...prev, data.CardDetails]);
                    } else {
                        // Update existing card
                        setCashCardList(prev => 
                            prev.map(card => 
                                card.ID === referenceId 
                                    ? { ...card, CardNo: data.CardDetails.CardNo } 
                                    : card
                            )
                        );
                        
                        // Reset update mode
                        resetForm();
                    }
                    
                    Swal.fire({
                        icon: 'success',
                        titleText: isUpdate ? 'Card Updated Successfully!' : 'Card Added Successfully!',
                        showConfirmButton: false,
                        timer: 1500
                    });
                } else {
                    Swal.fire({
                        icon: 'error',
                        titleText: 'Invalid Cash Card'
                    });
                }
            }
            fetchCashCardList(selectedMember?.value);
            setCardNumber('');
        } catch (error) {
            helper.StopProcessing(btn);
            Swal.fire({
                icon: 'error',
                titleText: error.message || 'Failed to save cash card'
            });
        }
    };

    const handleDeleteCard = async (id) => {
        try {
            const swalResult = await Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                buttonsStyling: false,
                reverseButtons: true,
                showLoaderOnConfirm: true,
                confirmButtonClass: 'btn btn-brand',
                cancelButtonClass: 'btn btn-secondary',
                confirmButtonText: 'Delete',
                preConfirm: async () => {
                    try {
                        await deleteCashCard(id);
                        return true;
                    } catch (err) {
                        Swal.showValidationMessage(`Request failed: ${err.message}`);
                        return false;
                    }
                },
            });
            
            if (swalResult.isConfirmed) {
                // Remove deleted card from list
                setCashCardList(prev => prev.filter(card => card.ID !== id));
                
                Swal.fire({
                    icon: 'success',
                    titleText: 'Card deleted successfully!',
                    showConfirmButton: false,
                    timer: 1500,
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                titleText: 'Error!',
                text: error.message,
                buttonsStyling: false,
                confirmButtonClass: 'btn btn-brand',
            });
        }
    };

    const handleEditCard = (card) => {
        setIsUpdate(true);
        setReferenceId(card.ID);
        setCardNumber(card.CardNo);
        $("#ddlMember").prop('disabled', true);
    };

    const resetForm = () => {
        setIsUpdate(false);
        setReferenceId(0);
        setCardNumber('');
        $("#ddlMember").prop('disabled', false);
        $('#ControlHolder').validate().resetForm();
    };

    return (
        <div className="kt-content kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor" id="kt_content">
            <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid">
                <div className="kt-portlet">
                    <div className="kt-portlet__body padding-t30 padding-b0">
                        <form id="ControlHolder" className="kt-form kt-form--label-right" role="form">
                            <div className="form-group row"> 
                                <label className="col-form-label col-lg-3">
                                    Member<span className="red"> *</span>
                                </label>
                                <div className="col-sm-3">
                                    <Select
                                        id="ddlMember"
                                        name="ddlMember"
                                        className="ddlMember"
                                        options={members}
                                        value={selectedMember}
                                        onChange={(value) => { setSelectedMember(value); setCardNumber('')}}
                                        isDisabled={isUpdate}
                                    />
                                </div>
                            </div>
                            <div className="form-group row">
                                <label className="col-form-label col-lg-3">
                                    Card Number<span className="red"> *</span>
                                </label>
                                <div className="col-sm-3">
                                    <input 
                                        type="text" 
                                        id="txtCardNumber" 
                                        name="txtCardNumber" 
                                        className="form-control"
                                        value={cardNumber}
                                        onChange={(e) => setCardNumber(e.target.value)}
                                    />
                                </div>
                                <div className="col-sm-3 padding-l20">
                                    {(!isUpdate) ? (
                                        <button 
                                            id="btnAdd" 
                                            type="button" 
                                            className="btn btn-label-brand btn-block"
                                            onClick={handleAddCard}
                                        >
                                            <i className="la la-plus"></i> Add
                                        </button>
                                    ) : (
                                        <div className="btn-group height-38">
                                            <button 
                                                id="btnAdd" 
                                                type="button" 
                                                className="btn btn-label-success d-inline-block margin-0"
                                                onClick={handleAddCard}
                                            >
                                                Update
                                            </button>
                                            <button 
                                                id="btnCancel" 
                                                type="button" 
                                                className="btn btn-secondary"
                                                onClick={resetForm}
                                            >
                                                <i className="la la-times ml-1 font-16"></i>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <hr className="divider" />

                            {/* Total Balance Information */}
                            {cashCardList.length > 0 && (
                                <div className="row" style={{color: '#535354', fontSize: '14px', marginBottom: '15px'}}>
                                    <div className="col-sm-12">
                                        <span style={{fontWeight: '400'}}>Total Balance</span>
                                        <strong style={{fontWeight: '600'}}> : ₹ {cardBalanceTotal.toFixed(2)}</strong>
                                    </div>
                                </div>
                            )}

                            {/* Table with mapped data */}
                            <div className="table-responsive">
                                <table id="table-cash-cards" className="table table-bordered table-hover margin-b30">
                                    <thead className="light-grey-background">
                                        <tr>
                                            <th>Card Number</th>
                                            <th>Balance</th>
                                            <th>Last Used</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cashCardList.length > 0 ? (
                                            cashCardList.map((card, i) => (
                                                <tr key={i}>
                                                    <td>{card.CardNo}</td>
                                                    <td><strong style={{fontWeight: '600'}}>₹ {card.CardAmt || 0}</strong></td>
                                                    <td>{card.LastUsed ? moment(card.LastUsed).format('DD-MMM-YYYY') : '-'}</td>
                                                    <td className="text-center">
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-outline-info btn-elevate btn-circle btn-icon btn-xs mr-2"
                                                            onClick={() => handleEditCard(card)}
                                                            disabled={isUpdate}
                                                        >
                                                            <i className="flaticon2-edit"></i>
                                                        </button>
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-outline-info btn-elevate btn-circle btn-icon btn-xs"
                                                            onClick={() => handleDeleteCard(card.ID)}
                                                            disabled={isUpdate}
                                                        >
                                                            <i className="flaticon2-rubbish-bin-delete-button"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={4}> 
                                                    <div className="padding-10"> 
                                                        <h5 className="text-center text-muted"> No Cash Cards Available! </h5> 
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </form>
                    </div>
                </div> 
            </div>
        </div>
    );
};

export default CashCardMaster;